import {pad} from "./utils";
import {DateTimeFormatter, Instant, LocalDateTime, nativeJs, ZoneId} from "@js-joda/core";
import "@js-joda/timezone";

export function durationSeconds(duration: number, durationUnit: string): number {
    switch (durationUnit) {
        case "SECONDS":
            return duration;
        case "MINUTES":
            return duration * 60;
        case "HOURS":
            return duration * 60 * 60;
        case "DAYS":
            return duration * 60 * 60 * 24;
        case "WEEKS":
            return duration * 60 * 60 * 24 * 7;
        case "MONTHS":
            return duration * 60 * 60 * 24 * 7 * 30;
        default:
            throw new Error("Not known time unit")
    }
}

export const dateTimeNow = (pattern: string) => formatDateTimeOrNow(pattern)


export function formatDateTimeOrNow(pattern: string, date?: Date | String | null) {
    if (date != null) {
        return formatDateTime(date, pattern)
    }

    return formatDateTime(new Date(), pattern)
}

export function formatDateTime(date: Date | String | null | undefined, pattern: string, defaultNone: string = "") {
    const tempDate = castToLocalDateTime(date)
    if (!tempDate) return defaultNone;

    return tempDate.format(DateTimeFormatter.ofPattern(pattern))
}

export function castToLocalDateTime(date: Date | String | null | undefined): LocalDateTime {
    if (date != null) {
        if (date instanceof Date) {
            return LocalDateTime.from(nativeJs(date))
        } else {
            return LocalDateTime.from(nativeJs(new Date(date as string)))
        }
    }

    return LocalDateTime.from(nativeJs(new Date()));
}

export function formatTime(date: Date | String | null | undefined) {
    let d: Date;
    if (date instanceof Date) {
        d = date;
    } else if (date instanceof String || typeof date === "string") {
        d = new Date(date as string);
    } else {
        return "";
    }

    return d.getFullYear() +
        '-' + pad(d.getMonth() + 1) +
        '-' + pad(d.getDate()) +
        ' ' + pad(d.getHours()) +
        ':' + pad(d.getMinutes()) +
        ':' + pad(d.getSeconds());
}

export function toDate(date: Date | String | string | undefined | null): Date | null {
    if (date) {
        if (date instanceof Date) return date;

        return new Date(date as string);
    } else {
        return null;
    }
}

export function formatDuration(secs: number) {
    let fmt = "";

    let sec_num = secs
    let days = Math.floor(sec_num / (3600 * 24));
    let hours = Math.floor((sec_num - (days * 3600 * 24)) / 3600);
    let minutes = Math.floor((sec_num - (days * 3600 * 24) - (hours * 3600)) / 60);
    let seconds = sec_num - (days * 3600 * 24) - (hours * 3600) - (minutes * 60);

    if (days > 0) {
        fmt += `${days}d `
    }
    if (hours > 0) {
        fmt += `${hours}h `
    }
    if (minutes > 0) {
        fmt += `${minutes}m `
    }
    if (seconds > 0) {
        fmt += `${seconds.toFixed(0)}s`
    }

    return fmt;
}

export function getDiffSeconds(date1: Date | String, date2: Date | String): number {
    let d1 = new Date(toDate(date1)!);
    let d2 = new Date(toDate(date2)!);

    return ((+d1) - (+d2)) / 1000;
}

function getMonday(date: Date) {
    let d = new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
    let day = d.getDay(),
        diff = d.getDate() - day + (day === 0 ? -6 : 1); // adjust when day is sunday
    return new Date(d.setDate(diff));
}

function getBeginningOfMonth(date: Date) {
    return new Date(date.getUTCFullYear(), date.getUTCMonth());
}

function getBeginningOfYear(date: Date) {
    return new Date(date.getUTCFullYear(), 0);
}

export const dateToReadableString = (date: Date | undefined, formatUntil?: ChronoUnit) => {
    let returnee = "";
    let tempDate = date ? date : new Date();

    returnee += tempDate.getFullYear();
    if (formatUntil === ChronoUnit.YEARS) {
        return returnee
    }

    returnee += "-" + ((tempDate.getMonth() + 1) >= 10 ? (tempDate.getMonth() + 1) : "0" + (tempDate.getMonth() + 1));
    if (formatUntil === ChronoUnit.MONTHS) {
        return returnee
    }

    returnee += "-" + (tempDate.getDate() >= 10 ? tempDate.getDate() : "0" + tempDate.getDate());
    if (formatUntil === ChronoUnit.DAYS) {
        return returnee
    }

    returnee += " " + (tempDate.getHours() >= 10 ? tempDate.getHours() : "0" + tempDate.getHours());
    if (formatUntil === ChronoUnit.HOURS) {
        return returnee
    }

    returnee += ":" + (tempDate.getMinutes() >= 10 ? tempDate.getMinutes() : "0" + tempDate.getMinutes());
    if (formatUntil === ChronoUnit.MINUTES) {
        return returnee
    }

    return returnee + ":" + (tempDate.getSeconds() >= 10 ? tempDate.getSeconds() : "0" + tempDate.getSeconds());
}

export enum ChronoUnit {
    MINUTES = 'MINUTES',
    HOURS = 'HOURS',
    DAYS = 'DAYS',
    WEEKS = 'WEEKS',
    MONTHS = 'MONTHS',
    YEARS = 'YEARS'
}

export enum ChronoUnitCompared {
    MINUTES = 60,
    HOURS = MINUTES * 60,
    DAYS = HOURS * 24,
    WEEKS = DAYS * 7,
    MONTHS = DAYS * 30,
    YEARS = MONTHS * 12
}

export function truncateDate(fromDate: Date, truncateTo: ChronoUnit | String | undefined): Date {
    let date = new Date(fromDate);

    if (truncateTo == null) {
        return date;
    }

    date.setMilliseconds(0);
    date.setSeconds(0);
    if (truncateTo === ChronoUnit.MINUTES) return date;

    date.setMinutes(0);
    if (truncateTo === ChronoUnit.HOURS) return date;

    date.setHours(0);
    if (truncateTo === ChronoUnit.DAYS) return date;

    date = getMonday(date);
    if (truncateTo === ChronoUnit.WEEKS) return date;

    date = getBeginningOfMonth(date);
    if (truncateTo === ChronoUnit.MONTHS) return date;

    date = getBeginningOfYear(date);
    if (truncateTo === ChronoUnit.YEARS) return date;

    return date;
}

export function addToDate(fromDate: Date, amount: number, unit: ChronoUnit | String | undefined): Date {
    let date = new Date(fromDate);

    if (amount == null || unit == null) {
        return date;
    }

    switch (unit) {
        case ChronoUnit.MINUTES:
            date.setMinutes(date.getMinutes() + amount);
            break;
        case ChronoUnit.HOURS:
            date.setHours(date.getHours() + amount);
            break;
        case ChronoUnit.DAYS:
            date.setHours(date.getHours() + amount * 24);
            break;
        case ChronoUnit.WEEKS:
            date.setHours(date.getHours() + amount * 24 * 7);
            break;
        case ChronoUnit.MONTHS:
            date.setMonth(date.getMonth() + amount);
            break;
        case ChronoUnit.YEARS:
            date.setFullYear(date.getFullYear() + amount);
            break;
    }

    return date;
}

export function dateWithoutTimeZone(date: Date | undefined) {
    if (date) {
        return addToDate(date, date.getTimezoneOffset() * -1, ChronoUnit.MINUTES)
    }
    let tempDate = new Date()
    return addToDate(tempDate, tempDate.getTimezoneOffset() * -1, ChronoUnit.MINUTES)
}

function chronoIsGreater(compared: ChronoUnit, comparedTo: ChronoUnit) {
    let comparedUnit = ChronoUnitCompared[compared];
    let comparedToUnit = ChronoUnitCompared[comparedTo];

    return comparedUnit.valueOf() > comparedToUnit.valueOf();
}


export namespace DateUtils {
    export const strFormatDateTime = (date: string, pattern: string, zoneId: string = "Europe/Vilnius") => {
        const ins = Instant.parse(date)
        return LocalDateTime.ofInstant(ins, ZoneId.of(zoneId)).format(DateTimeFormatter.ofPattern(pattern))
    }
}